import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { CLEN, ENRL, LANG, MutationResponse, SCLR, SLNG } from "@/models";
import {
  CHLD,
  mutationFunctions as chldMutationFunctions,
  clenMutationFunctions,
  enrlMutationFunctions,
  langMutationFunctions,
  slngMutationFunctions,
} from "@/models/chld";
import { type ErrorCode } from "@/models/error-codes.ts";
import {
  FAMI,
  mutationFunctions as famiMutationFunctions,
} from "@/models/fami";
import { type Anomaly, type AnomalyRun } from "@/models/file-submission.tsx";
import {
  SASS,
  sassMutationFunctions,
  sclrMutationFunctions,
  STFF,
  mutationFunctions as stffMutationFunctions,
} from "@/models/stff";
import {
  CLRM,
  mutationFunctions as clrmMutationFunctions,
} from "@/models/clrm";
import { useLeaData } from "@/queries";
import { useAuthenticatedSuspenseQuery } from "@/queries/shared";
import { type GridRowClickEvent } from "@progress/kendo-react-grid";
import { type UseSuspenseQueryResult } from "@tanstack/react-query";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { useCallback, useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  DiscrepancyDetails,
  ErrorDetails,
  getEntityId,
  getRecordIdLabel,
  RECORD_TYPE_NAMES,
} from "./useDiscrepancyPopup";
import { Tab, UpdateRecordParams } from "../DiscrepancyPopup";

export type EditDialogState =
  | { type: "CHLD"; data: CHLD }
  | { type: "ENRL"; data: ENRL }
  | { type: "CLEN"; data: CLEN }
  | { type: "FAMI"; data: FAMI }
  | { type: "LANG"; data: LANG }
  | { type: "SLNG"; data: SLNG }
  | { type: "STFF"; data: STFF }
  | { type: "SASS"; data: SASS }
  | { type: "SCLR"; data: SCLR }
  | { type: "CLRM"; data: CLRM }
  | null;

export type CurrentView = "none" | "discrepancy" | "edit";

const convertYesNo = (value: any): string => {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  return "";
};

const normalizeRecordData = <T extends Record<string, unknown>>(
  data: T,
  recordType: string
): T => {
  const normalized = { ...data };

  Object.keys(normalized).forEach((key) => {
    if (normalized[key] === null) {
      (normalized as any)[key] = "";
    }
  });

  if (recordType === "LANG") {
    (normalized as any).InstrumentOptOut = convertYesNo(
      (data as any).InstrumentOptOut
    );
    (normalized as any).DLLInstrumentDesignated = convertYesNo(
      (data as any).DLLInstrumentDesignated
    );
    (normalized as any).DLLTeacherDesignated = convertYesNo(
      (data as any).DLLTeacherDesignated
    );
  }

  if (recordType === "CHLD") {
    (normalized as any).ChildEthnicity = convertYesNo(
      (data as any).ChildEthnicity
    );
    (normalized as any).ChildEthnicityMissingIndicator =
      convertYesNo((data as any).ChildEthnicityMissingIndicator) || "Y";
    (normalized as any).ChildRaceMissingIndicator =
      convertYesNo((data as any).ChildRaceMissingIndicator) || "Y";
  }

  if (recordType === "STFF") {
    (normalized as any).StaffEthnicity = convertYesNo(
      (data as any).StaffEthnicity
    );
    (normalized as any).StaffEthnicityMissingIndicator =
      convertYesNo((data as any).StaffEthnicityMissingIndicator) || "Y";
    (normalized as any).StaffRaceMissingIndicator =
      convertYesNo((data as any).StaffRaceMissingIndicator) || "Y";
    (normalized as any).StaffWaiver = convertYesNo((data as any).StaffWaiver);
  }

  if (recordType === "FAMI") {
    (normalized as any).FamilyIncomeVerification = convertYesNo(
      (data as any).FamilyIncomeVerification
    );
  }

  if (recordType === "CLRM") {
    (normalized as any).MHCSOffered = convertYesNo((data as any).MHCSOffered);
  }

  return normalized;
};

export function useDiscrepanciesPage(certificationid?: string) {
  const { coreData } = useRouteContext({ from: "/$leaid" });
  const { leaid: leaId } = useParams({ from: "/$leaid" });
  const leaContext = useRouteContext({ from: "/$leaid" });
  const { data: leaData } = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const { addPendingFileSubmission, showToast } = useContext(
    FileSubmissionsContext
  );

  const [currentView, setCurrentView] = useState<CurrentView>("none");
  const [selectedDiscrepancy, setSelectedDiscrepancy] =
    useState<DiscrepancyDetails | null>(null);
  const [editDialog, setEditDialog] = useState<EditDialogState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [returnToTab, setReturnToTab] = useState<Tab>("recordData");

  const [chldResult, setChldResult] = useState<MutationResponse<CHLD> | null>(
    null
  );
  const [enrlResult, setEnrlResult] = useState<MutationResponse<ENRL> | null>(
    null
  );
  const [clenResult, setClenResult] = useState<MutationResponse<CLEN> | null>(
    null
  );
  const [famiResult, setFamiResult] = useState<MutationResponse<FAMI> | null>(
    null
  );
  const [langResult, setLangResult] = useState<MutationResponse<LANG> | null>(
    null
  );
  const [slngResult, setSlngResult] = useState<MutationResponse<SLNG> | null>(
    null
  );
  const [stffResult, setStffResult] = useState<MutationResponse<STFF> | null>(
    null
  );
  const [sassResult, setSassResult] = useState<MutationResponse<SASS> | null>(
    null
  );
  const [sclrResult, setSclrResult] = useState<MutationResponse<SCLR> | null>(
    null
  );
  const [clrmResult, setClrmResult] = useState<MutationResponse<CLRM> | null>(
    null
  );

  const chldMutateFns = chldMutationFunctions(leaData, setChldResult);
  const enrlMutateFns = enrlMutationFunctions(leaData, setEnrlResult);
  const clenMutateFns = clenMutationFunctions(leaData, setClenResult);
  const famiMutateFns = famiMutationFunctions(leaData, setFamiResult);
  const langMutateFns = langMutationFunctions(leaData, setLangResult);
  const slngMutateFns = slngMutationFunctions(leaData, setSlngResult);
  const stffMutateFns = stffMutationFunctions(leaData, setStffResult);
  const sassMutateFns = sassMutationFunctions(leaData, setSassResult);
  const sclrMutateFns = sclrMutationFunctions(leaData, setSclrResult);
  const clrmMutateFns = clrmMutationFunctions(leaData, setClrmResult);

  const chldForm = useForm<CHLD>();
  const enrlForm = useForm<ENRL>();
  const clenForm = useForm<CLEN>();
  const famiForm = useForm<FAMI>();
  const langForm = useForm<LANG>();
  const slngForm = useForm<SLNG>();
  const stffForm = useForm<STFF>();
  const sassForm = useForm<SASS>();
  const sclrForm = useForm<SCLR>();
  const clrmForm = useForm<CLRM>();

  const path = `/json/anomaly_run/${leaId}/0`;
  const { data: anomalyRun, error }: UseSuspenseQueryResult<AnomalyRun, Error> =
    useAuthenticatedSuspenseQuery(path, "filesubmissions");

  const discrepancies = anomalyRun?.Anomalies ?? [];

  const currentCalendar = coreData.reportingCalendar.find(
    (r) => r.CAPSDACsnpshtCode === certificationid
  );

  const fatalErrorCodes = coreData.certificationCycleAnomaly
    .filter(
      (a) =>
        a.CertificationCycleCode ===
        currentCalendar?.ErrorCertificationCycleCode
    )
    .map((a) => a.ErrorCode);

  const warningCodes = coreData.certificationCycleAnomaly
    .filter(
      (a) =>
        a.CertificationCycleCode ===
        currentCalendar?.WarningCertificationCycleCode
    )
    .map((a) => a.ErrorCode);

  const totalDiscrepancies = anomalyRun?.Detected ?? 0;

  const errorCodeCounts: ErrorCode[] = Object.entries(
    discrepancies.reduce<Record<string, number>>((acc, { ErrorCode }) => {
      acc[ErrorCode] = (acc[ErrorCode] || 0) + 1;
      return acc;
    }, {})
  ).map(([code, count]) => ({ code, count }));

  const countsByEntity = discrepancies.reduce<Map<string | undefined, number>>(
    (acc, discrepancy) => {
      const entityId = getEntityId(discrepancy);
      acc.set(entityId, (acc.get(entityId) ?? 0) + 1);
      return acc;
    },
    new Map<string | undefined, number>()
  );

  const tableData = discrepancies
    .filter((d) => {
      if (!certificationid || !currentCalendar) return true;
      return (
        fatalErrorCodes.includes(d.ErrorCode) ||
        warningCodes.includes(d.ErrorCode)
      );
    })
    .map((discrepancy) => {
      const entityId = getEntityId(discrepancy);
      const errorCount = countsByEntity.get(entityId) ?? 0;
      return {
        RecordID: getRecordIdLabel(discrepancy),
        ReferenceRecordType: discrepancy.ReferenceRecordType,
        ErrorCode: discrepancy.ErrorCode,
        ErrorMessage:
          coreData.errorMessageMap.get(discrepancy.ErrorCode)
            ?.ErrorMessageText ?? "--",
        ErrorCount: errorCount,
        _original: discrepancy,
      };
    });

  const handleCloseAll = useCallback(() => {
    setCurrentView("none");
    setSelectedDiscrepancy(null);
    setEditDialog(null);
    setReturnToTab("recordData");
  }, []);

  const handleRowClick = (event: GridRowClickEvent) => {
    const original = event.dataItem._original as Anomaly;
    const errorDetails = coreData.errorMessageMap.get(original.ErrorCode) as
      | ErrorDetails
      | undefined;

    const entityId = getEntityId(original);
    const relatedErrors = discrepancies.filter(
      (d) => getEntityId(d) === entityId
    );

    let referenceRecord = {};
    if (original.ReferenceRecord) {
      try {
        referenceRecord = JSON.parse(original.ReferenceRecord);
      } catch (e) {
        console.error("Failed to parse ReferenceRecord:", e);
      }
    }

    setSelectedDiscrepancy({
      anomaly: original,
      referenceRecord,
      errorDetails,
      relatedErrors,
    });
    setReturnToTab("recordData");
    setCurrentView("discrepancy");
  };

  const handleUpdateRecord = ({
    recordType,
    recordData,
    sourceTab,
  }: UpdateRecordParams) => {
    const normalizedData = normalizeRecordData(
      recordData as Record<string, unknown>,
      recordType
    );

    if (sourceTab) {
      setReturnToTab(sourceTab);
    }

    switch (recordType) {
      case "CHLD":
        chldForm.reset(normalizedData as unknown as CHLD);
        setEditDialog({
          type: "CHLD",
          data: normalizedData as unknown as CHLD,
        });
        break;
      case "ENRL":
        enrlForm.reset(normalizedData as unknown as ENRL);
        setEditDialog({
          type: "ENRL",
          data: normalizedData as unknown as ENRL,
        });
        break;
      case "CLEN":
        clenForm.reset(normalizedData as unknown as CLEN);
        setEditDialog({
          type: "CLEN",
          data: normalizedData as unknown as CLEN,
        });
        break;
      case "FAMI":
        famiForm.reset(normalizedData as unknown as FAMI);
        setEditDialog({
          type: "FAMI",
          data: normalizedData as unknown as FAMI,
        });
        break;
      case "LANG":
        langForm.reset(normalizedData as unknown as LANG);
        setEditDialog({
          type: "LANG",
          data: normalizedData as unknown as LANG,
        });
        break;
      case "SLNG":
        slngForm.reset(normalizedData as unknown as SLNG);
        setEditDialog({
          type: "SLNG",
          data: normalizedData as unknown as SLNG,
        });
        break;
      case "STFF":
        stffForm.reset(normalizedData as unknown as STFF);
        setEditDialog({
          type: "STFF",
          data: normalizedData as unknown as STFF,
        });
        break;
      case "SASS":
        sassForm.reset(normalizedData as unknown as SASS);
        setEditDialog({
          type: "SASS",
          data: normalizedData as unknown as SASS,
        });
        break;
      case "SCLR":
        sclrForm.reset(normalizedData as unknown as SCLR);
        setEditDialog({
          type: "SCLR",
          data: normalizedData as unknown as SCLR,
        });
        break;
      case "CLRM":
        clrmForm.reset(normalizedData as unknown as CLRM);
        setEditDialog({
          type: "CLRM",
          data: normalizedData as unknown as CLRM,
        });
        break;
      default:
        console.warn(`No edit handler for record type: ${recordType}`);
        return;
    }
    setCurrentView("edit");
  };

  const handleBackToDiscrepancy = () => {
    setCurrentView("discrepancy");
    setEditDialog(null);
  };

  const onSubmitChld: SubmitHandler<CHLD> = async (data) => {
    setIsSubmitting(true);
    try {
      await chldMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving CHLD:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitEnrl: SubmitHandler<ENRL> = async (data) => {
    setIsSubmitting(true);
    try {
      await enrlMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving ENRL:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitClen: SubmitHandler<CLEN> = async (data) => {
    setIsSubmitting(true);
    try {
      await clenMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving CLEN:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitFami: SubmitHandler<FAMI> = async (data) => {
    setIsSubmitting(true);
    try {
      await famiMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving FAMI:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitLang: SubmitHandler<LANG> = async (data) => {
    setIsSubmitting(true);
    try {
      await langMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving LANG:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitSlng: SubmitHandler<SLNG> = async (data) => {
    setIsSubmitting(true);
    try {
      await slngMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving SLNG:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitStff: SubmitHandler<STFF> = async (data) => {
    setIsSubmitting(true);
    try {
      await stffMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving STFF:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitSass: SubmitHandler<SASS> = async (data) => {
    setIsSubmitting(true);
    try {
      await sassMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving SASS:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitSclr: SubmitHandler<SCLR> = async (data) => {
    setIsSubmitting(true);
    try {
      await sclrMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving SCLR:", error);
      setIsSubmitting(false);
    }
  };

  const onSubmitClrm: SubmitHandler<CLRM> = async (data) => {
    setIsSubmitting(true);
    try {
      await clrmMutateFns.handleSave(data);
    } catch (error) {
      console.error("Error saving CLRM:", error);
      setIsSubmitting(false);
    }
  };

  const handleMutationResult = useCallback(
    (
      result: MutationResponse<any> | null,
      form: ReturnType<typeof useForm<any>>,
      setResult: React.Dispatch<
        React.SetStateAction<MutationResponse<any> | null>
      >,
      recordType: string
    ) => {
      if (result !== null) {
        if (result.fileSubmission !== null) {
          setIsSubmitting(false);
          handleCloseAll();
          showToast(
            "success",
            "Submitting",
            `${recordType} record is being submitted...`
          );
          addPendingFileSubmission(result.fileSubmission);
        } else if (result.errors && result.errors.length > 0) {
          setIsSubmitting(false);
          result.errors.forEach((e: any) =>
            form.setError(e.Fieldname, {
              type: "manual",
              message: e.Message,
            })
          );
        } else {
          setIsSubmitting(false);
          console.warn(`Unexpected mutation result for ${recordType}:`, result);
        }
        setResult(null);
      }
    },
    [handleCloseAll, showToast, addPendingFileSubmission]
  );

  useEffect(() => {
    handleMutationResult(chldResult, chldForm, setChldResult, "CHLD");
  }, [chldResult, chldForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(enrlResult, enrlForm, setEnrlResult, "ENRL");
  }, [enrlResult, enrlForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(clenResult, clenForm, setClenResult, "CLEN");
  }, [clenResult, clenForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(famiResult, famiForm, setFamiResult, "FAMI");
  }, [famiResult, famiForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(langResult, langForm, setLangResult, "LANG");
  }, [langResult, langForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(slngResult, slngForm, setSlngResult, "SLNG");
  }, [slngResult, slngForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(stffResult, stffForm, setStffResult, "STFF");
  }, [stffResult, stffForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(sassResult, sassForm, setSassResult, "SASS");
  }, [sassResult, sassForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(sclrResult, sclrForm, setSclrResult, "SCLR");
  }, [sclrResult, sclrForm, handleMutationResult]);

  useEffect(() => {
    handleMutationResult(clrmResult, clrmForm, setClrmResult, "CLRM");
  }, [clrmResult, clrmForm, handleMutationResult]);

  const handleSave = async () => {
    const dialog = editDialog;
    if (!dialog) return;

    const dialogType = dialog.type;

    switch (dialogType) {
      case "CHLD":
        chldForm.handleSubmit(onSubmitChld)();
        break;
      case "ENRL":
        enrlForm.handleSubmit(onSubmitEnrl)();
        break;
      case "CLEN":
        clenForm.handleSubmit(onSubmitClen)();
        break;
      case "FAMI":
        famiForm.handleSubmit(onSubmitFami)();
        break;
      case "LANG":
        langForm.handleSubmit(onSubmitLang)();
        break;
      case "SLNG":
        slngForm.handleSubmit(onSubmitSlng)();
        break;
      case "STFF":
        stffForm.handleSubmit(onSubmitStff)();
        break;
      case "SASS":
        sassForm.handleSubmit(onSubmitSass)();
        break;
      case "SCLR":
        sclrForm.handleSubmit(onSubmitSclr)();
        break;
      case "CLRM":
        clrmForm.handleSubmit(onSubmitClrm)();
        break;
      default:
        console.warn(`No save handler for record type: ${dialogType}`);
    }
  };

  const getRecordLabel = (type: string) => RECORD_TYPE_NAMES[type] || type;

  return {
    currentView,
    selectedDiscrepancy,
    editDialog,
    isSubmitting,
    returnToTab,

    coreData,
    discrepancies,
    totalDiscrepancies,
    errorCodeCounts,
    tableData,
    error,
    anomalyRun,

    fatalErrorCodes,
    warningCodes,

    forms: {
      chld: chldForm,
      enrl: enrlForm,
      clen: clenForm,
      fami: famiForm,
      lang: langForm,
      slng: slngForm,
      stff: stffForm,
      sass: sassForm,
      sclr: sclrForm,
      clrm: clrmForm,
    },

    submitHandlers: {
      chld: onSubmitChld,
      enrl: onSubmitEnrl,
      clen: onSubmitClen,
      fami: onSubmitFami,
      lang: onSubmitLang,
      slng: onSubmitSlng,
      stff: onSubmitStff,
      sass: onSubmitSass,
      sclr: onSubmitSclr,
      clrm: onSubmitClrm,
    },

    handleRowClick,
    handleCloseAll,
    handleUpdateRecord,
    handleBackToDiscrepancy,
    handleSave,
    getRecordLabel,
  };
}
